/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionApproveWorkflow';

const ActionApproveWorkflowManager: ActionManagerFn<'ActionApproveWorkflow'> = (
  platform: Platform,
): ActionManager<'ActionApproveWorkflow'> => {
  return {
    ...baseActionManager(platform, 'ActionApproveWorkflow'),
    isChildAllowed: (page, item, childType) =>
      ['ActionOnResponse'].includes(childType),
    defaultValue,
    props: {
      ...baseActionProps,
    },
    replaceableWith: ['ActionRejectWorkflow'],
    name: 'Approve Workflow',
    icon: { iconName: 'file-check' },
    description: `**Overview**<br/>
The Approve Workflow action is a specialized process component designed to signal the approval of a pending workflow step. It functions as a direct trigger to the workflow engine to transition the current instance to an "Approved" state.<br/><br/>
This action operates under strict environmental constraints: it is only functional when the hosting page has been accessed via a specific link generated by the wf.pageUrlForApproval() method within a workflow Approval node. It facilitates post-execution logic through a dedicated child action handler.

**Key Features**<br/>
* **Response Handling:** Explicitly restricts child components to ActionOnResponse (isChildAllowed), allowing developers to define logic that runs immediately after the server processes the approval.
* **Component Interchangeability:** Configured to be directly replaceable with ActionRejectWorkflow, allowing builders to switch between approval and rejection logic in the editor.
* **Contextual Response Data:** Documents response attributes available in the ActionOnResponse scope (via $._.response), including approvalStatus, approver, executionId, and wfInstUid.

**Properties**<br/>
* **Base Properties:** This component relies solely on ...baseActionProps, inheriting standard action configuration without defining unique inputs in the manager.

**Use Cases**<br/>
* **Decision Execution:** Clicking an "Approve" button on a task details page to advance a business process.
* **Post-Approval Feedback:** Using the On Response child action to display a success message (e.g., "Approved by [User]") using the returned server response data.

Use \`On Response\` child action to perform further actions after receiving the response from reject workflow call.

\`\`\`javascript
// The JSON Object from the server will be available as $._.response, which contains the following attributes
approvalStatus, // 'Approved',
approver, // Approver's userName,
executionId,
nodeId,
nodeType,
version, // Workflow UID
wfInstUid, // Workflow Instance UID
wfUid, // Workflow UID
// Sample expression using the above values sent by the server
\`\${$._.response.approvalStatus} \${$._.response.approver}!\`
\`\`\``,
  };
};

export default ActionApproveWorkflowManager;
